import React from 'react';
import { Link } from 'react-router-dom';
import './HomeV2.css';
import storyLandlord from '../assets/story-landlord.jpg';
import storyTenant from '../assets/story-tenant.jpg';
import useScrollReveal from '../hooks/useScrollReveal';
import PageSEO from './PageSEO';

function Platform() {
  const revealRef = useScrollReveal();

  const features = [
    {
      category: 'Tenant Screening',
      items: [
        { title: 'Credit checks', desc: 'Full credit report with score, payment history, outstanding debts, collections, and bankruptcies.' },
        { title: 'AI risk assessment', desc: 'Automated risk analysis with plain-English summary. Red flags, income ratios, and a clear recommendation.' },
        { title: 'Document verification', desc: 'Tenants upload paystubs, employment letters, and ID. AI extracts and cross-references data automatically.' },
        { title: 'Fraud detection', desc: 'AI flags inconsistencies between documents, self-reported info, and credit data.' },
        { title: 'Application pipeline', desc: 'Track every applicant from inquiry to approved. Status updates, notes, and side-by-side comparison.' },
      ]
    },
    {
      category: 'Leases',
      items: [
        { title: 'Ontario standard lease', desc: 'Auto-generated from the application. Pre-filled with tenant info, rent amount, and property details.' },
        { title: 'E-signatures', desc: 'Both landlord and tenant sign digitally. Completed lease stored securely in your document vault.' },
        { title: 'Lease renewals', desc: 'Track expiry dates. Renewal reminders sent automatically to both parties.' },
      ]
    },
    {
      category: 'Rent Payments',
      items: [
        { title: 'Card and bank payments', desc: 'Accept rent by credit card or pre-authorized debit (PAD). Powered by Stripe.' },
        { title: 'Autopay', desc: 'Tenants set up automatic payments. Rent collected on the due date without chasing.' },
        { title: 'Payment tracking', desc: 'Full rent ledger with payment history, outstanding balances, and receipts for both parties.' },
        { title: 'Off-platform tracking', desc: 'Tenants paying by e-transfer or cash? Track those payments too. One ledger for everything.' },
        { title: 'Late reminders', desc: 'Automatic reminders for overdue rent. Configurable timing and frequency.' },
        { title: 'Fee controls', desc: 'Choose who pays the processing fee - landlord absorbs it or passes it to the tenant.' },
      ]
    },
    {
      category: 'Property Management',
      items: [
        { title: 'Property listings', desc: 'Create shareable listing pages. Tenants apply online through your unique link.' },
        { title: 'Viewing scheduler', desc: 'Set available time slots. Tenants book viewings instantly. Reminders sent automatically.' },
        { title: 'Tenant messaging', desc: 'In-app messaging tied to properties and applications. No more scattered email threads.' },
        { title: 'Document vault', desc: 'All documents - leases, IDs, paystubs, consent forms - stored securely with role-based access.' },
        { title: 'Portfolio dashboard', desc: 'KPIs, rent collection status, application pipeline, and property overview at a glance.' },
        { title: 'Maintenance requests', desc: 'Tenants submit requests. Track status, priority, and resolution.' },
      ]
    },
    {
      category: 'Security and Compliance',
      items: [
        { title: 'Landlord identity verification', desc: 'Verified landlords build tenant trust. Stripe Identity powers the ID check.' },
        { title: 'PIPEDA compliant', desc: 'Built around Canadian privacy requirements. Tenant consent captured and logged for every credit check.' },
        { title: 'Encrypted documents', desc: 'Sensitive files encrypted in transit and at rest. Private Cloudinary storage with signed URLs.' },
        { title: 'Audit trail', desc: 'Every action logged - consent, payments, status changes, document access.' },
      ]
    }
  ];

  return (
    <main className="App-main v2-home" ref={revealRef}>
      <PageSEO title="Rental Management Platform for Ontario Landlords | Rentual" description="Screening, leases, payments, and property management in one platform. Built for Canadian landlords and property managers. Start free." path="/platform" />

      <section className="v2-hero" style={{ minHeight: 'auto', padding: '4rem 2rem 3rem' }}>
        <div className="v2-hero-inner">
          <h1 className="v2-reveal">
            The full platform.<br />
            <span className="v2-highlight">Everything you need to manage rentals.</span>
          </h1>
          <p className="v2-subhead v2-reveal" data-reveal-delay="100">
            Screening, leases, payments, and property management in one place. Built for Canadian landlords and property managers.
          </p>
          <div className="v2-ctas v2-reveal" data-reveal-delay="200">
            <Link to="/register" className="v2-cta-primary">Get started free</Link>
            <Link to="/pricing" className="v2-cta-secondary">View pricing</Link>
          </div>
        </div>
      </section>

      {/* ── Stories ──────────────────────── */}
      <section style={{ padding: '4rem 2rem', maxWidth: 1100, margin: '0 auto' }}>
        <div className="v2-story-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
          <div className="v2-reveal v2-reveal--left">
            <img src={storyLandlord} alt="Built for property managers" style={{ width: '100%', borderRadius: 16, border: '1px solid var(--v2-border, rgba(255,255,255,0.08))' }} />
          </div>
          <div className="v2-reveal v2-reveal--right">
            <p style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--v2-green, #28b6c8)', marginBottom: '0.75rem' }}>For operators</p>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.75rem' }}>Built for property managers</h3>
            <p style={{ color: 'var(--v2-muted, #888)', lineHeight: 1.6, marginBottom: '1rem' }}>
              Manage multi-property portfolios with team access, owner reporting, and centralized operations. From single buildings to city-wide portfolios.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Screen applicants with AI-powered dossiers and credit checks</li>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Collect rent by card with fee controls, autopay, and ledger tracking</li>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Assign team members, track performance, and report to owners</li>
            </ul>
          </div>
        </div>

        <div className="v2-story-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center', marginTop: '4rem' }}>
          <div className="v2-reveal v2-reveal--left">
            <p style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--v2-green, #28b6c8)', marginBottom: '0.75rem' }}>For tenants</p>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.75rem' }}>A better tenant experience</h3>
            <p style={{ color: 'var(--v2-muted, #888)', lineHeight: 1.6, marginBottom: '1rem' }}>
              Tenants get a guided experience from first viewing to move-in, with clear screening steps, fast communication, and online rent payments.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Submit applications and documents in one place</li>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Pay rent online and access receipts any time</li>
              <li style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}><span style={{ color: 'var(--v2-green)', fontWeight: 700 }}>&#10003;</span> Know exactly where you stand through screening and leasing</li>
            </ul>
          </div>
          <div className="v2-reveal v2-reveal--right">
            <img src={storyTenant} alt="A better tenant experience" style={{ width: '100%', borderRadius: 16, border: '1px solid var(--v2-border, rgba(255,255,255,0.08))' }} />
          </div>
        </div>
      </section>

      {features.map((section, i) => (
        <section key={i} className="v2-report" style={{ paddingTop: i === 0 ? '3rem' : '4rem' }}>
          <div className="v2-section-head v2-reveal">
            <h2>{section.category}</h2>
          </div>
          <div className="v2-report-grid">
            {section.items.map((item, j) => (
              <div key={j} className="v2-report-card v2-reveal" data-reveal-delay={j * 80}>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="v2-pricing v2-reveal" id="pricing">
        <div className="v2-section-head">
          <h2>Simple pricing</h2>
          <p>Screen tenants at $29/report. Unlock the full platform for $20/month.</p>
        </div>
        <div className="v2-ctas">
          <Link to="/pricing" className="v2-cta-primary">View pricing</Link>
        </div>
      </section>

      <section className="v2-final-cta v2-reveal">
        <h2>Ready to simplify your rental operations?</h2>
        <p>Start with a screening. Stay for the platform.</p>
        <div className="v2-ctas">
          <Link to="/register?intent=screen" className="v2-cta-primary">Screen a tenant</Link>
          <Link to="/register" className="v2-cta-secondary">Create free account</Link>
        </div>
      </section>

    </main>
  );
}

export default Platform;
